import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Box,
  HStack,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  Spinner,
  Center,
  Button,
} from '@chakra-ui/react';
import { Fragment, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  ChevronDownOutline,
  DEFAULT_DATE_FORMAT,
  PointsTransaction,
  SortArrowsAscBold,
  SortArrowsDescBold,
  individualsService,
  useToast,
  ReceiveSquareOutline,
  EyeOutline as Eye,
  ActionIconButton,
} from '@aslanpay/portal-common-lib';
import getPointsValue from 'app/utils/points';
import useDownloadGiftingReports from 'app/hooks/useDownloadGiftingReports';

interface RewardHistoryTableProps {
  transactions: PointsTransaction[];
}

interface GroupedTransaction {
  groupId: string;
  date: string;
  reason: string;
  displayReason: string;
  fullNotes: string;
  totalAmount: number;
  recipients: number;
  transactions: PointsTransaction[];
  individualIds: string[];
  transactionIds: string[];
}

interface IndividualData {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

function RewardHistoryTable({ transactions }: RewardHistoryTableProps) {
  const [selectedGroupedTransaction, setSelectedGroupedTransaction] =
    useState<GroupedTransaction | null>(null);
  const [order, setOrder] = useState<string>('DESC');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [displayedRecipients, setDisplayedRecipients] = useState<number>(15);
  const [isFetchingRecipients, setIsFetchingRecipients] = useState<boolean>(false);
  const [individualsData, setIndividualsData] = useState<Record<string, IndividualData | null>>({});
  const toast = useToast();

  // Download hook for gifting reports
  const { mutate: downloadGiftingReports } = useDownloadGiftingReports({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Gifting report downloaded successfully',
      });
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to download the file. Please try again.',
      });
    },
  });

  // Helper function to format currency without decimal places for the gifting history
  const formatCurrencyNoDecimals = (amount: number) => {
    return `£${amount
      .toLocaleString('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace('£', '')}`;
  };

  const fetchIndividualsData = async (individualIds: string[]) => {
    setIsFetchingRecipients(true);
    try {
      // Fetch individual data for each ID
      const individuals = await Promise.all(
        individualIds.map(async (id) => {
          try {
            // Use the individuals service from portal-common-lib
            const data = await individualsService.fetchIndividual(id);
            return { id, data };
          } catch (error) {
            // console.error(`Error fetching individual ${id}:`, error);
            return { id, data: null };
          }
        })
      );

      const individualsMap = individuals.reduce((acc, { id, data }) => {
        if (data) {
          acc[id] = {
            id: data.id,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
          };
        } else {
          acc[id] = null;
        }
        return acc;
      }, {} as Record<string, IndividualData | null>);

      setIndividualsData(individualsMap);
      setIsFetchingRecipients(false);
    } catch (error) {
      // console.error('Error fetching individuals data:', error);
      setIsFetchingRecipients(false);
    }
  };

  const openModal = (groupedTransaction: GroupedTransaction) => {
    const { individualIds } = groupedTransaction;

    setSelectedGroupedTransaction(groupedTransaction);
    setDisplayedRecipients(15);
    setIndividualsData({});
    onOpen();

    // Fetch individual data for all transactions in the group
    fetchIndividualsData(individualIds);
  };

  const sortClicked = () => {
    if (order === 'ASC') {
      setOrder('DESC');
    } else {
      setOrder('ASC');
    }
  };

  const getOrderedData = () => {
    if (order === 'ASC') {
      return [...transactions].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    }
    return [...transactions].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  };

  const groupTransactionsByGroupId = () => {
    const rawTransactions = getOrderedData();

    const grouped = rawTransactions.reduce((acc, transaction) => {
      let groupId = transaction.transactionGroupId;

      // If no group ID, create a unique one based on timestamp and labelName
      if (!groupId) {
        const timestamp = new Date(transaction.createdAt).getTime();
        const labelName = transaction.labelName || '';
        // Create a unique group ID based on timestamp (within 1 minute) and labelName
        const timeWindow = Math.floor(timestamp / 60000); // Group by minute
        groupId = `ungrouped_${timeWindow}_${labelName
          .substring(0, 20)
          .replace(/[^a-zA-Z0-9]/g, '')}`;
      }

      const date = format(parseISO(transaction.createdAt), DEFAULT_DATE_FORMAT);
      const labelName = transaction.labelName || 'Employee Gift';
      const notes = transaction.notes || '';

      const reason = labelName.length > 20 ? `${labelName.substring(0, 20)}...` : labelName;
      const displayReason = labelName; // Full label name for display

      if (!acc[groupId]) {
        acc[groupId] = {
          groupId,
          date,
          reason,
          displayReason,
          fullNotes: notes,
          totalAmount: 0,
          recipients: 0,
          transactions: [],
          individualIds: [], // Store individual IDs for fetching recipient data
          transactionIds: [], // Store transaction IDs for download functionality
        };
      }

      acc[groupId].totalAmount += transaction.amount;
      acc[groupId].recipients += 1;
      acc[groupId].transactions.push(transaction);
      acc[groupId].individualIds.push(transaction.individualId); // Store individual IDs for fetching recipient data
      acc[groupId].transactionIds.push(transaction.id); // Store transaction IDs for download functionality

      return acc;
    }, {} as Record<string, GroupedTransaction>);

    const result = Object.values(grouped).sort((a, b) => {
      const aTime = new Date(a.transactions[0].createdAt).getTime();
      const bTime = new Date(b.transactions[0].createdAt).getTime();
      return order === 'ASC' ? aTime - bTime : bTime - aTime;
    });

    return result;
  };

  const handleDownload = async (groupedTransaction: GroupedTransaction) => {
    const { groupId, recipients, transactionIds } = groupedTransaction;

    if (recipients === 1) {
      // Single employee: use transaction IDs
      downloadGiftingReports({ ids: transactionIds, size: 50000 });
    } else {
      // Multiple employees: use group ID
      downloadGiftingReports({ transactionGroupIds: [groupId], size: 50000 });
    }
  };

  const getRecipientDisplayData = (transaction: PointsTransaction) => {
    const individual = individualsData[transaction.individualId];

    if (individual) {
      const name =
        individual.firstName && individual.lastName
          ? `${individual.firstName} ${individual.lastName}`
          : individual.email || transaction.individualId || 'N/A';

      const email = individual.email || 'N/A';

      return { name, email };
    }

    // Fallback to individualId if no individual data available
    return {
      name: '',
      email: '',
    };
  };

  return (
    <Box overflowX="auto" w="full">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th onClick={sortClicked} cursor="pointer">
              <HStack spacing={2}>
                <Text textStyle="smallMedium" color="brand.black">
                  Date
                </Text>
                {order === 'DESC' ? (
                  <SortArrowsDescBold color="brand.darkGray" boxSize="15px" />
                ) : (
                  <SortArrowsAscBold color="brand.darkGray" boxSize="15px" />
                )}
              </HStack>
            </Th>
            <Th>
              <Text textStyle="smallMedium" color="brand.black">
                Total Gifted (£)
              </Text>
            </Th>
            <Th>
              <Text textStyle="smallMedium" color="brand.black">
                Recipients
              </Text>
            </Th>
            <Th>
              <Text textStyle="smallMedium" color="brand.black">
                Reason
              </Text>
            </Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {groupTransactionsByGroupId().map((groupedTransaction) => {
            return (
              <Fragment key={groupedTransaction.groupId}>
                <Tr
                  onClick={() => openModal(groupedTransaction)}
                  _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                >
                  <Td>
                    <Text textStyle="smallRegular" color="brand.darkGray">
                      {groupedTransaction.date}
                    </Text>
                  </Td>
                  <Td>
                    <Text textStyle="smallMedium" color="brand.darkGray" fontWeight="600">
                      {formatCurrencyNoDecimals(getPointsValue(groupedTransaction.totalAmount))}
                    </Text>
                  </Td>
                  <Td>
                    <Text textStyle="smallRegular" color="brand.darkGray">
                      {groupedTransaction.recipients}
                    </Text>
                  </Td>
                  <Td>
                    <Tooltip
                      label={`${
                        groupedTransaction.transactions[0]?.labelName || 'Employee Gift'
                      }: ${groupedTransaction.fullNotes || 'No additional notes'}`}
                      placement="top"
                    >
                      <Text textStyle="smallRegular" color="brand.darkGray">
                        {groupedTransaction.reason || 'Employee Gift'}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td textAlign="right">
                    <HStack spacing={2} justify="flex-end">
                      <ActionIconButton
                        aria-label="View details"
                        icon={<Eye boxSize="24px" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(groupedTransaction);
                        }}
                        color="brand.purple"
                        _hover={{ bg: 'brand.lightGray2' }}
                      />
                      <ActionIconButton
                        aria-label="Download"
                        icon={<ReceiveSquareOutline boxSize="24px" />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(groupedTransaction);
                        }}
                        color="brand.purple"
                        _hover={{ bg: 'brand.lightGray2' }}
                      />
                    </HStack>
                  </Td>
                </Tr>
              </Fragment>
            );
          })}
        </Tbody>
      </Table>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text color="brand.black" textStyle="h6SemiBold">
              Gift details
            </Text>
          </ModalHeader>
          <ModalCloseButton
            bg="transparent"
            color="brand.purple"
            border="2px solid"
            borderColor="brand.purple"
            borderRadius="full"
            size="sm"
            _hover={{ bg: 'brand.purple', color: 'white' }}
            _active={{ bg: 'brand.purple', color: 'white' }}
          />
          <ModalBody pb={6}>
            {/* Gift Summary Header */}
            <Box
              bg="brand.mutedPurple"
              color="white"
              p="12px 20px"
              borderRadius="5px"
              mb={4}
              display="flex"
              alignItems="center"
              gap="20px"
              alignSelf="stretch"
              overflow="hidden"
            >
              <Box minW="120px" flex="0 0 auto">
                <Text textStyle="smallRegular" color="white" opacity={0.8}>
                  Date
                </Text>
                <Text textStyle="smallMedium" color="white">
                  {selectedGroupedTransaction?.date}
                </Text>
              </Box>
              <Box minW="140px" flex="0 0 auto">
                <Text textStyle="smallRegular" color="white" opacity={0.8}>
                  Amount Gifted (£)
                </Text>
                <Text textStyle="smallMedium" color="white">
                  {formatCurrencyNoDecimals(
                    getPointsValue(selectedGroupedTransaction?.totalAmount || 0)
                  )}
                </Text>
              </Box>
              <Box flex="1" minW="0" overflow="hidden">
                <Text textStyle="smallRegular" color="white" opacity={0.8}>
                  Reason
                </Text>
                <Tooltip
                  label={`${
                    selectedGroupedTransaction?.transactions[0]?.labelName || 'Employee Gift'
                  }: ${selectedGroupedTransaction?.fullNotes || 'No additional notes'}`}
                  placement="top"
                >
                  <Text
                    textStyle="smallMedium"
                    color="white"
                    noOfLines={1}
                    textOverflow="ellipsis"
                    overflow="hidden"
                  >
                    {selectedGroupedTransaction?.transactions[0]?.labelName || 'Employee Gift'}
                  </Text>
                </Tooltip>
              </Box>
            </Box>

            {/* Recipient List */}
            {isFetchingRecipients ? (
              <Center width="full" height="full" flexGrow={1}>
                <Spinner color="brand.purple" emptyColor="brand.tint30" size="xl" />
              </Center>
            ) : (
              <Box>
                <Flex justify="space-between" align="center" mb={4}>
                  <Text textStyle="h5SemiBold" color="brand.darkPurple">
                    Recipients
                  </Text>
                  <Text color="brand.darkGray" textAlign="center" textStyle="smallMedium">
                    Showing{' '}
                    {Math.min(
                      displayedRecipients,
                      selectedGroupedTransaction?.transactions.length || 0
                    )}{' '}
                    of {selectedGroupedTransaction?.transactions.length || 0} results
                  </Text>
                </Flex>

                <Box
                  maxH="400px"
                  overflowY="auto"
                  border="1px solid"
                  borderColor="brand.lightGray1"
                  borderRadius="md"
                >
                  <Table variant="simple" size="sm">
                    <Thead position="sticky" top={0} bg="brand.lightGray2" zIndex={1}>
                      <Tr>
                        <Th>
                          <Text color="brand.black" textStyle="extraSmallSemiBold">
                            Recipient name
                          </Text>
                        </Th>
                        <Th>
                          <Text color="brand.black" textStyle="extraSmallSemiBold">
                            Email
                          </Text>
                        </Th>
                        <Th textAlign="right">
                          <Text color="brand.black" textStyle="extraSmallSemiBold">
                            Gift amount
                          </Text>
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {selectedGroupedTransaction?.transactions
                        .slice(0, displayedRecipients)
                        .map((transaction: PointsTransaction) => {
                          const { name, email } = getRecipientDisplayData(transaction);

                          return (
                            <Tr key={transaction.id}>
                              <Td>
                                <Text
                                  overflow="hidden"
                                  color="brand.darkGray"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                  textStyle="extraSmallMedium"
                                >
                                  {name}
                                </Text>
                              </Td>
                              <Td>
                                <Text
                                  overflow="hidden"
                                  color="brand.purple"
                                  textOverflow="ellipsis"
                                  textStyle="extraSmallMedium"
                                >
                                  {email}
                                </Text>
                              </Td>
                              <Td textAlign="right">
                                <Text
                                  overflow="hidden"
                                  color="brand.darkGray"
                                  textOverflow="ellipsis"
                                  whiteSpace="nowrap"
                                  textStyle="extraSmallMedium"
                                >
                                  {formatCurrencyNoDecimals(getPointsValue(transaction.amount))}
                                </Text>
                              </Td>
                            </Tr>
                          );
                        })}
                    </Tbody>
                  </Table>
                </Box>

                {/* See More Button - Always show, but disable when at the end */}
                <Center mt={4}>
                  <Button
                    variant="outline"
                    colorScheme="brand"
                    leftIcon={<ChevronDownOutline />}
                    onClick={() => {
                      const currentCount = displayedRecipients;
                      const totalCount = selectedGroupedTransaction?.transactions.length || 0;
                      // Always increment by 15 to maintain consistent list length
                      setDisplayedRecipients(Math.min(currentCount + 15, totalCount));
                    }}
                    size="sm"
                    isDisabled={
                      displayedRecipients >= (selectedGroupedTransaction?.transactions.length || 0)
                    }
                  >
                    {displayedRecipients >= (selectedGroupedTransaction?.transactions.length || 0)
                      ? 'All recipients shown'
                      : 'See more'}
                  </Button>
                </Center>
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default RewardHistoryTable;
